// apps/web/src/pages/CreateBankPage.tsx
// Phase 2 实现 — 创建题库页面

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { createBankApi } from "@/api/banks";

const schema = z.object({
  name: z.string().min(1, "请输入题库名称").max(100, "名称最多100字"),
  description: z.string().max(500, "描述最多500字").optional().or(z.literal("")),
  tag: z.string().optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

export default function CreateBankPage() {
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: createBankApi,
    onSuccess: (data) => {
      toast.success("题库创建成功");
      navigate(`/banks/${data.id}`, { replace: true });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "创建失败");
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "", tag: "" },
  });

  const onSubmit = (vals: FormValues) => {
    mutation.mutate({
      name: vals.name,
      description: vals.description || undefined,
      tag: vals.tag || undefined,
    });
  };

  return (
    <div className="min-h-screen bg-white px-6 py-4">
      {/* Back */}
      <Link
        to="/banks"
        className="inline-flex items-center text-primary text-sm mb-6 hover:text-primary-dark"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4 mr-1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        返回题库列表
      </Link>

      <h1 className="text-xl font-bold mb-6">创建题库</h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            题库名称 <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            {...register("name")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="如：计算机网络期末"
            maxLength={100}
          />
          {errors.name && (
            <p className="text-danger text-xs mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
          <textarea
            {...register("description")}
            rows={3}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50 resize-none"
            placeholder="可选，简要描述题库内容"
            maxLength={500}
          />
          {errors.description && (
            <p className="text-danger text-xs mt-1">{errors.description.message}</p>
          )}
        </div>

        {/* Tag */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">标签</label>
          <input
            type="text"
            {...register("tag")}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
                       text-sm bg-gray-50/50"
            placeholder="如：期末、考研、CPA"
          />
          {errors.tag && (
            <p className="text-danger text-xs mt-1">{errors.tag.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold
                     rounded-xl transition-colors disabled:opacity-50 btn-tap mt-6"
        >
          {mutation.isPending ? "创建中..." : "创建题库"}
        </button>

        {/* Error */}
        {mutation.isError && (
          <p className="text-danger text-sm text-center">
            {(mutation.error as Error).message ?? "创建失败"}
          </p>
        )}
      </form>
    </div>
  );
}
